import { t } from 'i18next'
import { ReactNode, useMemo } from 'react'
import Moment from 'react-moment'
import { useParams } from 'react-router-dom'
import { styled } from 'styled-components'

import { FileTextOutlined, LayoutOutlined } from '@ant-design/icons'
import {
  BackButton,
  Badge,
  BoldText,
  SelectConformityCheckLabQrStatus,
  SmartFunctionRunStatusIcon,
} from '../../../../components'
import {
  BadgeColors,
  ConformityCheckLabQrStatus,
  NO_DATA_PLACEHOLDER,
  algorithmRunStatuses,
  dateFormat,
  smartFunctionBadgeStatusColorKeys,
  workspaceDocumentSetStatuses,
} from '../../../../constants/constants'
import {
  useAlgorithmRunDetails,
  useUsers,
  useWorkspaceDetails,
} from '../../../../hooks'
import {
  AlgorithmRunConformityCheckConfig,
  ConformityCheckQualityReviewItem,
} from '../../../../store/reducers/workspaceReducer'
import { AlgorithmRunStatus } from '../../../../types/generalTypes'
import SmartFunctionActionDropdown from '../../components/SmartFunctionActionDropdown/SmartFunctionActionDropdown'
import SmartFunctionSelectRun from '../../components/SmartFunctionSelectRun'

export const SmartFunctionSubHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 12px;
  gap: 8px;
`

export const SmartFunctionMainSubHeader = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

export const SmartFunctionSubHeaderLine = styled.div`
  display: flex;
  flex: 1;
  gap: 8px;
  color: ${(props) => props.theme.token.colorText};
`

export const SmartFunctionSubHeaderCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const SmartFunctionSubHeaderCellContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  margin-left: auto;
`

export const SmartFunctionSubHeaderCellSection = styled.div`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.token.colorText};
`

const SmartFunctionHeaderFirstRow = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  margin-bottom: 8px;
`

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  margin-top: 8px;
`

const CreatorContainer = styled.div`
  display: flex;
  align-items: center;
`

const SmartFunctionSubHeaderFirstRowWrapper = styled.div`
  display: flex;
  width: 100%;
  gap: 16px;
`

interface ConformityCheckQualityReviewSmartFunctionDetailsPageHeaderProps {
  backButtonUrl: string
  children?: ReactNode
  onRunChange?: (value?: string) => void
  title?: string
  filter?: ConformityCheckLabQrStatus
  onFilterChange?: (value?: ConformityCheckLabQrStatus) => void
  deviations?: ConformityCheckQualityReviewItem[]
}

const ConformityCheckQualityReviewSmartFunctionDetailsPageHeader = ({
  backButtonUrl,
  onRunChange,
  title,
  filter,
  onFilterChange,
  deviations,
}: ConformityCheckQualityReviewSmartFunctionDetailsPageHeaderProps) => {
  const { workspaceId, algorithmRunId, documentId } = useParams()

  const { selectedAlgorithmRun } = useAlgorithmRunDetails({
    algorithmRunId,
    workspaceId,
    preventFetch: true,
  })

  const { selectedWorkspace } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })

  const { findUser } = useUsers({})

  const selectedDocumentFile = useMemo(() => {
    const file = (
      selectedAlgorithmRun?.config as AlgorithmRunConformityCheckConfig
    )?.iterationFilename
    if (file) {
      return file
    }
    return NO_DATA_PLACEHOLDER
  }, [selectedAlgorithmRun])

  const templateTitle = useMemo(() => {
    return (selectedAlgorithmRun?.config as AlgorithmRunConformityCheckConfig)
      ?.documentTemplateName
  }, [selectedAlgorithmRun])

  const isHighlighted = useMemo(() => {
    const isRunComplete =
      selectedAlgorithmRun?.runStatus === algorithmRunStatuses.COMPLETE

    const isDocumentOpen =
      selectedWorkspace?.config?.conformityChecksConfig?.documents?.find(
        (doc) => doc.id === documentId
      )?.documentSetStatus !== workspaceDocumentSetStatuses.CLOSED

    const hasDeviations = deviations?.some(
      (deviation) =>
        deviation.documentId === documentId &&
        deviation.algorithmRunId === algorithmRunId &&
        deviation.isDeviation
    )

    return isRunComplete && isDocumentOpen && hasDeviations
  }, [
    selectedAlgorithmRun,
    selectedWorkspace,
    documentId,
    deviations,
    algorithmRunId,
  ])

  return (
    <SmartFunctionSubHeader>
      <SmartFunctionSubHeaderFirstRowWrapper>
        <BackButton
          url={backButtonUrl}
          tooltipMessage={t(
            'smartFunctionDetailsPage.backButtonToIntelligentSystem'
          )}
          type="default"
        />
        <SmartFunctionMainSubHeader>
          <SmartFunctionHeaderFirstRow>
            <SmartFunctionSubHeaderLine>
              <SmartFunctionSubHeaderCell>
                <BoldText>
                  {title ||
                    selectedWorkspace?.algorithmName ||
                    NO_DATA_PLACEHOLDER}
                </BoldText>
              </SmartFunctionSubHeaderCell>

              <SmartFunctionSubHeaderCellContainer>
                <SmartFunctionSubHeaderCellSection>
                  {selectedAlgorithmRun?.actionDate ? (
                    <Moment
                      local
                      date={selectedAlgorithmRun?.actionDate}
                      format={dateFormat.PRIMARY}
                    />
                  ) : (
                    NO_DATA_PLACEHOLDER
                  )}
                </SmartFunctionSubHeaderCellSection>

                <SmartFunctionSubHeaderCellSection>
                  <SmartFunctionSelectRun onChange={onRunChange} />
                </SmartFunctionSubHeaderCellSection>

                <SmartFunctionSubHeaderCellSection>
                  <Badge
                    color={
                      smartFunctionBadgeStatusColorKeys[
                        selectedAlgorithmRun?.runStatus as AlgorithmRunStatus
                      ]
                    }
                    icon={
                      <SmartFunctionRunStatusIcon
                        status={
                          selectedAlgorithmRun?.runStatus as AlgorithmRunStatus
                        }
                      />
                    }
                  >
                    {selectedAlgorithmRun?.runStatus
                      ? t(
                          `workspaceDetailsPage.status.${selectedAlgorithmRun.runStatus}`
                        )
                      : NO_DATA_PLACEHOLDER}
                  </Badge>
                </SmartFunctionSubHeaderCellSection>

                <SmartFunctionSubHeaderCellSection>
                  <SmartFunctionActionDropdown
                    key={selectedAlgorithmRun?.id}
                    algorithmRun={selectedAlgorithmRun}
                    deviations={deviations}
                    showFlagDeviationsMenuItem
                    isHighlighted={isHighlighted}
                  />
                </SmartFunctionSubHeaderCellSection>
              </SmartFunctionSubHeaderCellContainer>
            </SmartFunctionSubHeaderLine>
          </SmartFunctionHeaderFirstRow>
        </SmartFunctionMainSubHeader>
      </SmartFunctionSubHeaderFirstRowWrapper>
      <SmartFunctionSubHeaderLine>
        <StatusContainer>
          <SelectConformityCheckLabQrStatus
            defaultValue={filter}
            onChange={onFilterChange}
          />

          <Badge
            color={BadgeColors.TEMPLATE_ANNOTATIONS}
            icon={<LayoutOutlined />}
          >
            {templateTitle}
          </Badge>

          <Badge
            color={BadgeColors.PROCESSING}
            icon={<FileTextOutlined />}
          >
            {selectedDocumentFile}
          </Badge>
        </StatusContainer>

        <CreatorContainer>
          {findUser(selectedWorkspace?.createdBy || '')?.displayName ||
            NO_DATA_PLACEHOLDER}
        </CreatorContainer>
      </SmartFunctionSubHeaderLine>
    </SmartFunctionSubHeader>
  )
}

export default ConformityCheckQualityReviewSmartFunctionDetailsPageHeader
